import React, { useState, useEffect } from "react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { 
  Loader2, X, Bell, MessageSquare, Trash2
} from "lucide-react";
import { apiClient } from "../../../App";
import { toast } from "sonner";

const MessagesTab = ({ password }) => {
  // Messages/Broadcast state
  const [broadcasts, setBroadcasts] = useState([]);
  const [broadcastsLoading, setBroadcastsLoading] = useState(false);
  const [showCreateBroadcast, setShowCreateBroadcast] = useState(false);
  const [newBroadcast, setNewBroadcast] = useState({
    title: '',
    message: '',
    image_url: '',
    link_url: '',
    link_text: '',
    target_audience: 'all',
    scheduled_date: '',
    scheduled_time: ''
  });
  const [expirySettings, setExpirySettings] = useState({
    days_before_expiry: 3,
    pre_expiry_message: 'Hi {name}! Your AlphaTradeVission subscription expires in {days} days. Renew now!',
    post_expiry_message: 'Hi {name}! Your AlphaTradeVission subscription has expired. Renew now to get back access!',
    enabled: true
  });
  const [expiringUsers, setExpiringUsers] = useState({ expiring_soon: [], recently_expired: [] });
  const [showExpiryReminders, setShowExpiryReminders] = useState(false);
  const [broadcastLinks, setBroadcastLinks] = useState(null);
  const [schedulerStatus, setSchedulerStatus] = useState(null);

  // Fetch broadcasts on mount
  useEffect(() => {
    fetchBroadcasts();
    fetchSchedulerStatus();
  }, []);

  const fetchSchedulerStatus = async () => {
    try {
      const response = await apiClient.get(`/admin/notification-scheduler/status?admin_password=${encodeURIComponent(password)}`);
      setSchedulerStatus(response.data);
    } catch (err) {
      console.error('Failed to fetch scheduler status:', err);
    }
  };

  const fetchBroadcasts = async () => {
    setBroadcastsLoading(true);
    try {
      const [broadcastsRes, settingsRes, expiringRes] = await Promise.all([
        apiClient.get(`/admin/broadcasts?admin_password=${encodeURIComponent(password)}`),
        apiClient.get(`/admin/expiry-notification-settings?admin_password=${encodeURIComponent(password)}`),
        apiClient.get(`/admin/users-expiring?admin_password=${encodeURIComponent(password)}&days=7`)
      ]);
      setBroadcasts(broadcastsRes.data.broadcasts || []);
      setExpirySettings(settingsRes.data);
      setExpiringUsers(expiringRes.data);
    } catch (err) {
      console.error('Failed to fetch broadcasts:', err);
    }
    setBroadcastsLoading(false);
  };

  const createBroadcast = async () => {
    if (!newBroadcast.title || !newBroadcast.message) {
      toast.error('Please enter title and message');
      return;
    }
    try {
      const response = await apiClient.post('/admin/broadcast/create', {
        admin_password: password,
        ...newBroadcast
      });
      if (response.data.success) {
        toast.success(response.data.message);
        setShowCreateBroadcast(false);
        setNewBroadcast({
          title: '', message: '', image_url: '', link_url: '', link_text: '',
          target_audience: 'all', scheduled_date: '', scheduled_time: ''
        });
        fetchBroadcasts();
      }
    } catch (err) {
      toast.error('Failed to create broadcast');
    }
  };

  const deleteBroadcast = async (broadcastId) => {
    if (!window.confirm('Delete this broadcast?')) return;
    try {
      await apiClient.delete(`/admin/broadcast/${broadcastId}?admin_password=${encodeURIComponent(password)}`);
      toast.success('Broadcast deleted');
      fetchBroadcasts();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const sendBroadcast = async (broadcastId) => {
    try {
      const response = await apiClient.post(`/admin/broadcast/${broadcastId}/send?admin_password=${encodeURIComponent(password)}`);
      if (response.data.success) {
        setBroadcastLinks(response.data);
        toast.success(`Ready to send to ${response.data.total_users} users!`);
      }
    } catch (err) {
      toast.error('Failed to prepare broadcast');
    }
  };

  const saveExpirySettings = async () => {
    try {
      await apiClient.post('/admin/expiry-notification-settings', {
        admin_password: password,
        ...expirySettings
      });
      toast.success('Expiry notification settings saved!');
    } catch (err) {
      toast.error('Failed to save settings');
    }
  };

  const getExpiryReminders = async () => {
    try {
      const response = await apiClient.post(`/admin/send-expiry-reminders?admin_password=${encodeURIComponent(password)}`);
      if (response.data.success) {
        setShowExpiryReminders(true);
        setExpiringUsers({
          expiring_soon: response.data.pre_expiry.users,
          recently_expired: response.data.post_expiry.users
        });
        toast.success(`Found ${response.data.pre_expiry.count} expiring and ${response.data.post_expiry.count} expired users`);
      }
    } catch (err) {
      toast.error('Failed to get reminders');
    }
  };

  const triggerManualCheck = async () => {
    try {
      const response = await apiClient.post(`/admin/notification-scheduler/trigger?admin_password=${encodeURIComponent(password)}`);
      if (response.data.success) {
        toast.success('Expiry check triggered! Results sent via Telegram.');
        fetchSchedulerStatus();
      }
    } catch (err) {
      toast.error('Failed to trigger check');
    }
  };

  if (broadcastsLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-teal-400" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Scheduler Status */}
      {schedulerStatus && (
        <div className="p-3 bg-teal-500/10 border border-teal-500/30 rounded-lg">
          <div className="flex justify-between items-center">
            <h3 className="text-sm font-semibold text-teal-400 flex items-center gap-2">
              <Bell className="w-4 h-4" />
              Auto Notification Scheduler
              <span className={`ml-2 px-2 py-0.5 text-[10px] rounded ${schedulerStatus.is_running ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                {schedulerStatus.is_running ? 'RUNNING' : 'STOPPED'}
              </span>
            </h3>
            <Button size="sm" onClick={triggerManualCheck} className="bg-teal-600 text-xs h-7">
              Trigger Now
            </Button>
          </div>
          {schedulerStatus.jobs && schedulerStatus.jobs.length > 0 && (
            <div className="mt-2 text-[10px] text-[#6b7a99]">
              {schedulerStatus.jobs.map((job, idx) => (
                <div key={idx}>
                  {job.name}: Next run at {job.next_run ? new Date(job.next_run).toLocaleString() : 'N/A'}
                </div>
              ))}
            </div>
          )}
          {schedulerStatus.last_run && (
            <div className="mt-1 text-[10px] text-[#6b7a99]">
              Last run: {new Date(schedulerStatus.last_run).toLocaleString()}
            </div>
          )}
        </div>
      )}

      {/* Expiry Notifications Section */}
      <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
        <div className="flex justify-between items-center mb-2">
          <h3 className="text-sm font-semibold text-amber-400 flex items-center gap-2">
            <Bell className="w-4 h-4" />
            Subscription Expiry Alerts
          </h3>
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-[#6b7a99]">
              {expiringUsers.expiring_soon?.length || 0} expiring, {expiringUsers.recently_expired?.length || 0} expired
            </span>
            <Button
              size="sm"
              onClick={getExpiryReminders}
              className="bg-amber-600 hover:bg-amber-700 text-xs h-7"
            >
              Send Reminders
            </Button>
          </div>
        </div>
        
        {/* Expiry Settings */}
        <div className="space-y-2 mb-3">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={expirySettings.enabled}
              onChange={(e) => setExpirySettings({...expirySettings, enabled: e.target.checked})}
            />
            <Label className="text-[#6b7a99] text-xs">Enable expiry notifications</Label>
            <Input
              type="number"
              min="1"
              max="30"
              value={expirySettings.days_before_expiry}
              onChange={(e) => setExpirySettings({...expirySettings, days_before_expiry: parseInt(e.target.value) || 3})}
              className="w-16 bg-[#0a0e17] border-[#1a2540] text-white rounded text-xs h-7"
            />
            <span className="text-[10px] text-[#6b7a99]">days before</span>
          </div>
          <div>
            <Label className="text-[#6b7a99] text-[10px]">Pre-expiry message (use {'{name}'} and {'{days}'})</Label>
            <Input
              type="text"
              value={expirySettings.pre_expiry_message}
              onChange={(e) => setExpirySettings({...expirySettings, pre_expiry_message: e.target.value})}
              className="mt-1 bg-[#0a0e17] border-[#1a2540] text-white rounded text-xs"
              placeholder="Hi {name}! Your subscription expires in {days} days..."
            />
          </div>
          <div>
            <Label className="text-[#6b7a99] text-[10px]">Post-expiry message (use {'{name}'})</Label>
            <Input
              type="text"
              value={expirySettings.post_expiry_message}
              onChange={(e) => setExpirySettings({...expirySettings, post_expiry_message: e.target.value})}
              className="mt-1 bg-[#0a0e17] border-[#1a2540] text-white rounded text-xs"
              placeholder="Hi {name}! Your subscription has expired..."
            />
          </div>
          <Button size="sm" onClick={saveExpirySettings} className="bg-amber-600 text-xs h-7">
            Save Settings
          </Button>
        </div>
        
        {/* Expiry Reminder Links */}
        {showExpiryReminders && (expiringUsers.expiring_soon?.length > 0 || expiringUsers.recently_expired?.length > 0) && (
          <div className="mt-3 space-y-2 max-h-40 overflow-y-auto">
            {expiringUsers.expiring_soon?.map((user, idx) => (
              <div key={idx} className="flex justify-between items-center p-2 bg-[#0a0e17] rounded text-xs">
                <div>
                  <span className="text-white">{user.name}</span>
                  <span className="ml-2 text-yellow-400">Expires in {user.days_remaining} days</span>
                </div>
                {user.whatsapp_link && (
                  <Button size="sm" onClick={() => window.open(user.whatsapp_link, '_blank')} className="bg-green-600 text-[10px] h-6">
                    WhatsApp
                  </Button>
                )}
              </div>
            ))}
            {expiringUsers.recently_expired?.map((user, idx) => (
              <div key={`exp-${idx}`} className="flex justify-between items-center p-2 bg-[#0a0e17] rounded text-xs">
                <div>
                  <span className="text-white">{user.name}</span>
                  <span className="ml-2 text-red-400">Expired</span>
                </div>
                {user.whatsapp_link && (
                  <Button size="sm" onClick={() => window.open(user.whatsapp_link, '_blank')} className="bg-green-600 text-[10px] h-6">
                    WhatsApp
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Create Broadcast Section */}
      <div className="flex justify-between items-center">
        <h3 className="text-sm font-semibold text-white flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-teal-400" />
          Broadcast Messages
        </h3>
        <Button
          onClick={() => setShowCreateBroadcast(!showCreateBroadcast)}
          size="sm"
          className="bg-teal-600 hover:bg-teal-700 text-white text-xs"
        >
          {showCreateBroadcast ? 'Cancel' : '+ Create Message'}
        </Button>
      </div>
      
      {/* Create Broadcast Form */}
      {showCreateBroadcast && (
        <div className="p-3 bg-teal-500/10 border border-teal-500/30 rounded-lg space-y-3">
          <div>
            <Label className="text-[#6b7a99] text-xs">Title *</Label>
            <Input
              type="text"
              value={newBroadcast.title}
              onChange={(e) => setNewBroadcast({...newBroadcast, title: e.target.value})}
              className="mt-1 bg-[#0a0e17] border-[#1a2540] text-white rounded-lg text-sm"
              placeholder="Festival Greetings / New Feature Alert / etc."
            />
          </div>
          <div>
            <Label className="text-[#6b7a99] text-xs">Message *</Label>
            <textarea
              value={newBroadcast.message}
              onChange={(e) => setNewBroadcast({...newBroadcast, message: e.target.value})}
              className="mt-1 w-full bg-[#0a0e17] border border-[#1a2540] text-white rounded-lg text-sm p-2 min-h-[80px]"
              placeholder="Write your message here... You can include multiple lines for better formatting."
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-[#6b7a99] text-xs">Image URL (optional)</Label>
              <Input
                type="url"
                value={newBroadcast.image_url}
                onChange={(e) => setNewBroadcast({...newBroadcast, image_url: e.target.value})}
                className="mt-1 bg-[#0a0e17] border-[#1a2540] text-white rounded-lg text-sm"
                placeholder="https://i.imgur.com/..."
              />
            </div>
            <div>
              <Label className="text-[#6b7a99] text-xs">Link URL (optional)</Label>
              <Input
                type="url"
                value={newBroadcast.link_url}
                onChange={(e) => setNewBroadcast({...newBroadcast, link_url: e.target.value})}
                className="mt-1 bg-[#0a0e17] border-[#1a2540] text-white rounded-lg text-sm"
                placeholder="https://yoursite.com/..."
              />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-[#6b7a99] text-xs">Target Audience</Label>
              <select
                value={newBroadcast.target_audience}
                onChange={(e) => setNewBroadcast({...newBroadcast, target_audience: e.target.value})}
                className="mt-1 w-full bg-[#0a0e17] border border-[#1a2540] text-white rounded-lg text-sm p-2"
              >
                <option value="all">All Users</option>
                <option value="active">Active Subscribers</option>
                <option value="expired">Expired Users</option>
                <option value="expiring_soon">Expiring Soon</option>
              </select>
            </div>
            <div>
              <Label className="text-[#6b7a99] text-xs">Schedule Date</Label>
              <Input
                type="date"
                value={newBroadcast.scheduled_date}
                onChange={(e) => setNewBroadcast({...newBroadcast, scheduled_date: e.target.value})}
                className="mt-1 bg-[#0a0e17] border-[#1a2540] text-white rounded-lg text-sm"
              />
            </div>
            <div>
              <Label className="text-[#6b7a99] text-xs">Schedule Time</Label>
              <Input
                type="time"
                value={newBroadcast.scheduled_time}
                onChange={(e) => setNewBroadcast({...newBroadcast, scheduled_time: e.target.value})}
                className="mt-1 bg-[#0a0e17] border-[#1a2540] text-white rounded-lg text-sm"
              />
            </div>
          </div>
          <Button
            onClick={createBroadcast}
            className="w-full bg-gradient-to-r from-teal-500 to-cyan-600 text-white text-xs"
          >
            {newBroadcast.scheduled_date ? 'Schedule Broadcast' : 'Create Broadcast'}
          </Button>
        </div>
      )}
      
      {/* Broadcast Links Modal */}
      {broadcastLinks && (
        <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
          <div className="flex justify-between items-center mb-2">
            <h4 className="text-sm font-semibold text-green-400">
              Ready to Send: {broadcastLinks.total_users} users ({broadcastLinks.users_with_phone} with WhatsApp)
            </h4>
            <Button size="sm" variant="ghost" onClick={() => setBroadcastLinks(null)}>
              <X className="w-4 h-4" />
            </Button>
          </div>
          <div className="bg-[#0a0e17] p-2 rounded mb-2">
            <p className="text-[10px] text-[#6b7a99] mb-1">Message Preview:</p>
            <p className="text-xs text-white whitespace-pre-line">{broadcastLinks.broadcast?.message}</p>
          </div>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {broadcastLinks.whatsapp_links?.map((user, idx) => (
              <div key={idx} className="flex justify-between items-center p-2 bg-[#0a0e17] rounded text-xs">
                <div>
                  <span className="text-white">{user.name}</span>
                  <span className="ml-2 text-[#6b7a99]">{user.phone}</span>
                </div>
                <Button size="sm" onClick={() => window.open(user.link, '_blank')} className="bg-green-600 text-[10px] h-6">
                  Send WhatsApp
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Broadcasts List */}
      <div className="space-y-2">
        {broadcasts.length === 0 ? (
          <p className="text-center text-[#6b7a99] text-xs py-4 bg-[#111827] rounded">
            No broadcasts yet. Create your first message to send to all users!
          </p>
        ) : (
          broadcasts.map((b) => (
            <div key={b.broadcast_id} className="p-3 bg-[#111827] border border-[#1a2540] rounded-lg">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h4 className="text-sm font-semibold text-white">{b.title}</h4>
                  <p className="text-[10px] text-[#6b7a99]">
                    {b.target_audience === 'all' ? 'All Users' : 
                     b.target_audience === 'active' ? 'Active Subscribers' :
                     b.target_audience === 'expired' ? 'Expired Users' : 'Expiring Soon'}
                    {b.scheduled_datetime && ` • Scheduled: ${b.scheduled_datetime}`}
                  </p>
                </div>
                <span className={`text-[9px] px-2 py-0.5 rounded ${
                  b.status === 'ready' ? 'bg-green-500/20 text-green-400' :
                  b.status === 'scheduled' ? 'bg-blue-500/20 text-blue-400' :
                  'bg-gray-500/20 text-gray-400'
                }`}>
                  {b.status?.toUpperCase() || 'PENDING'}
                </span>
              </div>
              <p className="text-xs text-[#6b7a99] mb-2 line-clamp-2">{b.message}</p>
              {(b.image_url || b.link_url) && (
                <div className="flex gap-2 mb-2">
                  {b.image_url && <span className="text-[9px] text-cyan-400">Has Image</span>}
                  {b.link_url && <span className="text-[9px] text-cyan-400">Has Link</span>}
                </div>
              )}
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => sendBroadcast(b.broadcast_id)}
                  className="bg-green-600 hover:bg-green-700 text-xs flex-1"
                >
                  Send Now
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => deleteBroadcast(b.broadcast_id)}
                  className="text-xs"
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
              {b.sent_count > 0 && (
                <p className="text-[9px] text-green-400 mt-1">Sent to {b.sent_count} users</p>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default MessagesTab;
